import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { Toaster } from 'react-hot-toast';

// Context
import { AuthProvider } from './contexts/AuthContext';
import { CurrencyProvider } from './contexts/CurrencyContext';

// Components
import ProtectedRoute from './components/ProtectedRoute';
import DashboardLayout from './components/layout/DashboardLayout';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import Dashboard from './components/dashboard/Dashboard';
import WebsitesPage from './components/websites/WebsitesPage';
import SchemasPage from './components/schemas/SchemasPage';
import OrdersPage from './components/orders/OrdersPage';
import FormEmailsPage from './components/emails/FormEmailsPage';
import AssetsPage from './components/assets/AssetsPage';
import SettingsPage from './components/settings/SettingsPage';
import SuperAdminPage from './components/admin/SuperAdminPage';
import SuperAdminUserDetailPage from './components/admin/SuperAdminUserDetailPage';
import SuperAdminWebsiteDetailPage from './components/admin/SuperAdminWebsiteDetailPage';
import SuperAdminOrdersPage from './components/admin/SuperAdminOrdersPage';
import SuperAdminFormsPage from './components/admin/SuperAdminFormsPage';

// Create theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#667eea',
      light: '#9bb5ff',
      dark: '#3f51b5',
    },
    secondary: {
      main: '#764ba2',
      light: '#a478d4',
      dark: '#4a2c73',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 700,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 600,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <CurrencyProvider>
          <Router>
            <div className="App">
              <Routes>
                {/* Public Routes */}
                <Route
                  path="/login"
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <Login />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/register"
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <Register />
                    </ProtectedRoute>
                  }
                />

                {/* Protected Routes */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <DashboardLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Dashboard />} />
                  <Route
                    path="superadmin"
                    element={<SuperAdminPage />}
                  />
                  <Route
                    path="superadmin/orders"
                    element={<SuperAdminOrdersPage />}
                  />
                  <Route
                    path="superadmin/forms"
                    element={<SuperAdminFormsPage />}
                  />
                  <Route
                    path="superadmin/users/:id"
                    element={<SuperAdminUserDetailPage />}
                  />
                  <Route
                    path="superadmin/websites/:id"
                    element={<SuperAdminWebsiteDetailPage />}
                  />
                  <Route
                    path="websites"
                    element={<WebsitesPage />}
                  />
                  <Route
                    path="schemas"
                    element={<SchemasPage />}
                  />
                  <Route
                    path="orders"
                    element={<OrdersPage />}
                  />
                  <Route
                    path="emails"
                    element={<FormEmailsPage />}
                  />
                  <Route
                    path="assets"
                    element={<AssetsPage />}
                  />
                  <Route
                    path="profile"
                    element={
                      <div>
                        <h2>Profile Page - Coming Soon</h2>
                        <p>This page will allow you to edit your profile.</p>
                      </div>
                    }
                  />
                  <Route
                    path="settings"
                    element={<SettingsPage />}
                  />
                </Route>

                {/* Default redirect */}
                <Route path="/" element={<Navigate to="/dashboard" replace />} />

                {/* 404 Route */}
                <Route
                  path="*"
                  element={
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                      <h1>404 - Page Not Found</h1>
                      <p>The page you're looking for doesn't exist.</p>
                    </div>
                  }
                />
              </Routes>
            </div>
          </Router>
        </CurrencyProvider>

        {/* Toast Notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              theme: {
                primary: '#4aed88',
              },
            },
            error: {
              duration: 4000,
              theme: {
                primary: '#ff6b6b',
              },
            },
          }}
        />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
